<template>
  <div class="momentum-conservation-pro-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>Σp_before = Σp_after | 系统不受外力$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
               <polygon points="0 0, 10 3.5, 0 7" fill="#fff" />
             </marker>
          </defs>
          
          <line x1="0" y1="180" x2="400" y2="180" stroke="#444" stroke-width="2" />

          <!-- Block 1 -->
          <g :transform="`translate(${b1.x}, 160)`">
             <rect x="-20" y="-20" width="40" height="40" :fill="b1.color" rx="4" />
             <text y="-25" text-anchor="middle" fill="#fff" font-size="10">{{ b1.m }} kg</text>
             <line v-if="active" x1="0" y1="0" :x2="b1.v * 10" y2="0" stroke="#fff" stroke-width="2" marker-end="url(#arrowhead)" />
          </g>

          <!-- Block 2 -->
          <g :transform="`translate(${b2.x}, 160)`">
             <rect x="-20" y="-20" width="40" height="40" :fill="b2.color" rx="4" />
             <text y="-25" text-anchor="middle" fill="#fff" font-size="10">{{ b2.m }} kg</text>
             <line v-if="active" x1="0" y1="0" :x2="b2.v * 10" y2="0" stroke="#fff" stroke-width="2" marker-end="url(#arrowhead)" />
          </g>

          <!-- Data Overlay -->
          <g transform="translate(20, 40)">
             <text x="0" y="0" fill="#888" font-size="10">系统总动量 P = m1v1 + m2v2</text>
             <text x="0" y="20" fill="#ffd700" font-size="14" font-weight="bold">ΣP = {{ totalP.toFixed(2) }} kg·m/s</text>
          </g>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>过程状态: {{ phase }}</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center gap-4">
             <div class="flex-1">
                <label>质量比 (m1:m2)</label>
                <el-slider v-model="massRatio" :min="0.1" :max="5" :step="0.1" size="mini"></el-slider>
             </div>
             <div class="flex-1">
                <label>初速度 v1</label>
                <el-slider v-model="initV1" :min="1" :max="15" size="mini"></el-slider>
             </div>
             <el-button type="primary" size="mini" @click="start" :disabled="active">释放</el-button>
             <el-button size="mini" @click="reset">重置</el-button>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>动量守恒定律是物理学中最基本、最普遍的守恒定律之一。它规定：如果一个系统不受外力，或者所受外力的矢量和为零，这个系统的总动量保持不变。即使系统内部发生剧烈的相互作用（如碰撞、爆炸、排斥），只要满足条件，始末动量完全一致。这表明了**空间平移对称性**。通过调节质量和速度，你可以观察到无论如何碰撞，总动量表盘数值始终恒定。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MomentumConservationProViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      massRatio: 1,
      initV1: 10,
      active: false,
      phase: '准备就绪',
      b1: { x: 50, v: 0, m: 2, color: '#ff4757' },
      b2: { x: 250, v: 0, m: 2, color: '#1e90ff' },
      timer: null
    }
  },
  computed: {
    totalP() {
      return this.b1.m * this.b1.v + this.b2.m * this.b2.v;
    }
  },
  watch: {
    massRatio(val) {
      this.b1.m = val * 2;
      this.b2.m = 2;
      this.reset();
    },
    initV1(val) { // eslint-disable-line no-unused-vars
      this.reset();
    }
  },
  methods: {
    start() {
      this.active = true;
      this.b1.v = this.initV1;
      this.b2.v = 0;
      this.phase = '碰撞中...';
      this.animate();
    },
    animate() {
      if (!this.active) return;
      
      this.b1.x += this.b1.v * 0.5;
      this.b2.x += this.b2.v * 0.5;

      // Collision detection
      if (Math.abs(this.b1.x - this.b2.x) < 40 && this.b1.v > this.b2.v) {
         // Perfectly elastic collision:
         let v1 = this.b1.v;
         let v2 = this.b2.v;
         let m1 = this.b1.m;
         let m2 = this.b2.m;
         
         this.b1.v = ((m1 - m2) * v1 + 2 * m2 * v2) / (m1 + m2);
         this.b2.v = ((m2 - m1) * v2 + 2 * m1 * v1) / (m1 + m2);
         this.phase = '后碰撞飞离';
      }

      if (this.b1.x > 450 || this.b2.x > 450 || this.b1.x < -50 || this.b2.x < -50) {
         this.active = false;
         this.phase = '运动结束 (动量守恒已验证)';
         return;
      }

      this.timer = requestAnimationFrame(this.animate);
    },
    reset() {
      this.active = false;
      this.b1.x = 50;
      this.b1.v = 0;
      this.b2.x = 250;
      this.b2.v = 0;
      this.phase = '准备就绪';
      cancelAnimationFrame(this.timer);
    }
  },
  mounted() {
    this.b1.m = this.massRatio * 2;
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.momentum-conservation-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(46, 213, 115, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
